import * as React from "react";
import { useCallback, useEffect, useRef } from "react";
import { atom, useAtom, useSetAtom } from "jotai";
import { Album } from "../scripts/album";
import { DAO } from "../scripts/dao/dao";
import { useLocale } from "../scripts/i18n";
import { useCloseScore } from "../scripts/scores";
import { useQueryParam } from "../scripts/state";
import { useToggleTheme } from "../scripts/theme";
import { Tune } from "../scripts/tune";
import { activateRipple } from "./commons/ripple";
import { Cross } from "./icon/Icons";
import { ctxMenuAtom } from "./ctxMenuAtom";
import { useGlobalEventListener } from "./utils";

export const menuOpenAtom = atom(false);

function useMenuOpenState(): [boolean, (b: boolean) => void, () => boolean] {
  const [isMenuOpen, setMenuOpen] = useAtom(menuOpenAtom);
  const isMenuOpenDyn = useRef<boolean>();
  useEffect(() => {
    isMenuOpenDyn.current = isMenuOpen;
  }, [isMenuOpen]);
  return [isMenuOpen, setMenuOpen, () => isMenuOpenDyn.current];
}

export const Menubar: React.FC = () => {
  const [i18n] = useLocale();
  const [isMenuOpen, setMenuOpen, getIsMenuOpen] = useMenuOpenState();
  const [q] = useQueryParam();
  const state = q.has("score") ? "Score" : "Album";
  const itemId = q.get("score") ?? q.get("album");
  const closeScore = useCloseScore();
  const toggleTheme = useToggleTheme();

  useGlobalEventListener(
    () => document,
    "click",
    (e) => {
      if (!(e.target as HTMLElement).closest("#menubar") && getIsMenuOpen()) {
        setMenuOpen(false);
        e.preventDefault();
      }
    },
    false
  );

  const onOpenScoreFile = useCallback(
    (e: React.MouseEvent) => {
      DAO.openOriginal({ id: itemId });
      activateRipple(e.target as HTMLElement, () => {
        setMenuOpen(false);
      });
    },
    [itemId]
  );

  function onCloseTab(e: React.MouseEvent) {
    activateRipple(e.target as HTMLElement, () => {
      closeScore(itemId);
    });
  }

  function onCloseMenu(e: React.MouseEvent) {
    activateRipple(e.target as HTMLElement, () => {
      setMenuOpen(false);
    });
  }

  function onToggleTheme(e: React.MouseEvent) {
    activateRipple(e.target as HTMLElement, () => {
      toggleTheme();
    });
  }

  function onCopyCurrentUrl(e: React.MouseEvent) {
    activateRipple(e.target as HTMLElement, () => {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(document.URL);
        // showToast(i18n.menu.toastCopiedCurrentUrl);
      }
    });
  }

  return (
    <div id="menubar" hidden={!isMenuOpen}>
      <div id="menu-close-button" onClick={onCloseMenu}>
        <Cross />
      </div>
      <div className="menu_item" onClick={onOpenScoreFile}>
        {i18n.menu.openOriginal}
      </div>
      <div
        className="menu_item"
        hidden={state !== "Score"}
        onClick={onCloseTab}
      >
        {i18n.menu.closeScore}
      </div>
      <div className="menu_item" onClick={onToggleTheme}>
        {i18n.menu.toggleTheme}
      </div>
      <div className="menu_item" onClick={onCopyCurrentUrl}>
        {i18n.menu.copyCurrentUrl}
      </div>
    </div>
  );
};

export const Ctxmenu: React.FC<{
  item: Album | Tune;
  shown: boolean;
}> = ({ item, shown }) => {
  const setContextMenu = useSetAtom(ctxMenuAtom);
  const [i18n] = useLocale();
  const onOpenScoreFile = (e: React.MouseEvent) => {
    DAO.openOriginal(item);
    activateRipple(e.target as HTMLElement, () => {
      // setMenuOpen(false);
    });
  };
  const onCloseCtxMenu = (e) => {
    setContextMenu(null);
  };
  return (
    <div className="ctxmenu_wrap">
      {shown ? (
        <>
          <div className="ctxmenu" onClick={onCloseCtxMenu}></div>
          <div className="menu_item" onClick={onOpenScoreFile}>
            {i18n.menu.openOriginal}
          </div>
          <div className="menu_item menu_item_close" onClick={onCloseCtxMenu}>
            <Cross />
          </div>
        </>
      ) : (
        <></>
      )}
    </div>
  );
};
